import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

import { Store } from '@ngrx/store';

import { Observable, Subscription } from 'rxjs';

import { AppState } from '../../../../state';
import {
  deleteModuleItem,
  saveModuleItemSortingOrder,
  updateModuleItem,
} from '../../../state/module-item/module-item.actions';
import { selectSelectedLanguage, selectSelectedStudy } from '../../../state/study/study.selectors';
import { selectSelectedModule } from '../../../state/module/module.selectors';
import {
  selectIsLoadingModuleItemsConfig,
  selectModuleItemsConfig,
  selectModuleItemsConfigLoaded,
  selectSavedModuleItem,
  selectSortedModuleItems,
} from '../../../state/module-item/module-item.selectors';
import { Study } from '../../../../models/study';
import { Module } from '../../../../models/module';
import { Language } from '../../../../models/language';
import { StudyType } from '../../../../models/study-type';
import { ModuleItem } from '../../../../models/module-item';
import { ModuleItemsConfig } from '../../../../models/module-items-config';
import {
  ModuleItemForbiddenSubTypesCC,
  ModuleItemForbiddenSubTypesSubscription,
  ModuleItemForbiddenSubTypesTIIM,
  ModuleItemSubType,
} from '../../../../models/module-item-sub-type';
import {
  CALCULATED_VARIABLE_FUNCTIONS,
  PRIVILEGE_ACTIONS,
  PRIVILEGE_SECTIONS,
  RANDOMIZATION_VALUES,
} from '../../../../core/dashboard-constants';
import { ModuleItemDialogComponent } from '../module-item-dialog/module-item-dialog.component';
import { PrivilegeService } from '../../../services/privilege/privilege.service';
import {
  getModuleItemIconBySubType,
  getModuleItemSortOrderArray,
  getModuleItemSubTypeLabel,
} from '../../helpers/module-item-helper';
import { PreviewService } from '../../../services/preview/preview.service';
import { cannotContainSpace } from '../../../../core/custom-form-validators';
import { selectCalculatedVariables } from '../../../state/calculated-variable/calculated-variable.selectors';
import { CalculatedVariable } from '../../../../models/calculated-variable';
import { selectStudy } from '../../../state/study/study.actions';
import { updateCalculatedVariable } from '../../../state/calculated-variable/calculated-variable.actions';

@Component({
  selector: 'dashboard-module-item-list',
  templateUrl: './module-item-list.component.html',
  styleUrls: ['./module-item-list.component.scss'],
})
export class ModuleItemListComponent implements OnInit, OnDestroy {
  @Input() isScreening: boolean;

  currentDropEvent: CdkDragDrop<ModuleItemSubType[]>;

  calculatedVariableFunctions = CALCULATED_VARIABLE_FUNCTIONS;
  lettersReg = /[a-zA-Z]/g;

  loadingModuleItemsConfig$: Observable<boolean>;
  moduleItemsConfigLoaded$: Observable<boolean>;

  calculatedVariableForm: FormGroup;
  studyCalculatedVariables: CalculatedVariable[];

  selectedStudy: Study;
  selectedLanguage: Language;
  selectedModule: Module;
  moduleItemsConfig: ModuleItemsConfig;

  selectedModuleItems: ModuleItem[];

  availableModuleItems: ModuleItemSubType[] = [];
  itemToDrop: ModuleItemSubType = null;

  questionLengthLimit = 20;

  itemToDuplicate = null;
  isEditModuleItem = false;
  isFocused = false;

  PRIVILEGE_SECTIONS = PRIVILEGE_SECTIONS;
  PRIVILEGE_ACTIONS = PRIVILEGE_ACTIONS;
  RANDOMIZATION_VALUES = RANDOMIZATION_VALUES;

  private subscriptions = new Subscription();

  calculationNamesWithModuleItemIds: {
    calculationName: string;
    moduleItemId: number;
  }[] = [];

  ngOnInit(): void {
    this.loadingModuleItemsConfig$ = this.store.select(selectIsLoadingModuleItemsConfig);
    this.moduleItemsConfigLoaded$ = this.store.select(selectModuleItemsConfigLoaded);

    this.calculatedVariableForm = this.formBuilder.group({
      calculatedVariables: this.formBuilder.array([]),
    });

    this.subscriptions.add(
      this.store
        .select(selectSelectedLanguage)
        .subscribe((selectedLanguage) => (this.selectedLanguage = selectedLanguage))
    );
    this.subscriptions.add(
      this.store.select(selectSelectedStudy).subscribe((selectedStudy) => {
        this.selectedStudy = selectedStudy;
        this.selectedStudy.modules?.forEach((module: Module) => {
          module.moduleItems?.forEach((moduleItem: ModuleItem) => {
            if (moduleItem.forCalculation) {
              this.calculationNamesWithModuleItemIds.push({
                calculationName: moduleItem.calculationName,
                moduleItemId: moduleItem.id,
              });
            }
          });
        });
      })
    );

    this.subscriptions.add(
      this.store.select(selectCalculatedVariables).subscribe((calculatedVariables) => {
        if (calculatedVariables) {
          this.studyCalculatedVariables = calculatedVariables;
        }
      })
    );

    this.subscriptions.add(
      this.store.select(selectSelectedModule).subscribe((selectedModule) => (this.selectedModule = selectedModule))
    );

    this.subscriptions.add(
      this.store.select(selectSortedModuleItems).subscribe((moduleItems) => {
        this.selectedModuleItems = moduleItems;
        this.clearCalculatedVariables();
        for (const item of this.selectedModuleItems) {
          this.addCalculatedVariableForm(item.calculationName);
        }
      })
    );

    this.subscriptions.add(
      this.store.select(selectModuleItemsConfig).subscribe((moduleItemsConfig) => {
        this.moduleItemsConfig = moduleItemsConfig;
        if (this.moduleItemsConfig) {
          this.availableModuleItems = [];
          this.populateAvailableModuleItemsArray();
        }
      })
    );
    this.subscriptions.add(
      this.store.select(selectSavedModuleItem).subscribe((savedModuleItem) => {
        if ((savedModuleItem && this.itemToDrop && this.currentDropEvent) || this.itemToDuplicate) {
          const newItemIndex = this.selectedModuleItems.findIndex((item) => item.id === savedModuleItem.id);
          if (this.itemToDuplicate) {
            const duplicatedItemIndex = this.selectedModuleItems.findIndex(
              (item) => item?.id === this.itemToDuplicate?.id
            );
            duplicatedItemIndex < newItemIndex
              ? moveItemInArray(this.selectedModuleItems, newItemIndex, duplicatedItemIndex + 1)
              : moveItemInArray(this.selectedModuleItems, newItemIndex, duplicatedItemIndex);
            this.itemToDuplicate = null;
          } else if (!this.isEditModuleItem) {
            moveItemInArray(this.selectedModuleItems, newItemIndex, this.currentDropEvent.currentIndex);
          }
          this.saveModuleSortingOrder(this.selectedModuleItems);
        }
      })
    );
  }

  constructor(
    private formBuilder: FormBuilder,
    private store: Store<AppState>,
    private dialog: MatDialog,
    private previewService: PreviewService,
    public privilegeService: PrivilegeService
  ) {}

  get calculatedVariables(): FormArray {
    return this.calculatedVariableForm.get('calculatedVariables') as FormArray;
  }

  clearCalculatedVariables(): void {
    while (this.calculatedVariables.length !== 0) {
      this.calculatedVariables.removeAt(0);
    }
  }

  addCalculatedVariableForm(calculationName: string) {
    const calculatedVariable = this.formBuilder.group({
      calculatedName: [calculationName, [Validators.required, Validators.maxLength(40), cannotContainSpace]],
    });
    this.calculatedVariables.push(calculatedVariable);
  }

  populateAvailableModuleItemsArray(): void {
    this.moduleItemsConfig?.itemTypes.forEach((itemType) => {
      itemType?.subTypes.forEach((subType) => {
        switch (this.selectedStudy?.type) {
          case StudyType.TIIM:
            if (!ModuleItemForbiddenSubTypesTIIM.some((subType1) => subType1 === subType.type)) {
              if (this.isScreening) {
                if (!ModuleItemForbiddenSubTypesSubscription.some((subType1) => subType1 === subType.type)) {
                  this.availableModuleItems.push({ ...subType, itemType: itemType.type });
                }
              } else {
                this.availableModuleItems.push({ ...subType, itemType: itemType.type });
              }
            }
            break;
          case StudyType.CC:
            if (!ModuleItemForbiddenSubTypesCC.some((subType1) => subType1 === subType.type)) {
              this.availableModuleItems.push({ ...subType, itemType: itemType.type });
            }
            break;
          default:
            break;
        }
      });
    });
  }

  dropItem(event): void {
    this.currentDropEvent = event;
    if (event.container.id === 'availableItemsList') {
      return;
    }
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      moveItemInArray(this.calculatedVariables.controls, event.previousIndex, event.currentIndex);
      this.saveModuleSortingOrder(event.container.data);
    }
  }

  moveModuleItem(selectedModuleItemSubType: ModuleItemSubType): void {
    this.itemToDrop = selectedModuleItemSubType;
    this.currentDropEvent = {
      ...this.currentDropEvent,
      currentIndex: this.selectedModuleItems.length,
    };
    this.openModuleItemDialog(null, selectedModuleItemSubType);
  }

  openModuleItemDialog(moduleItem?: ModuleItem, moduleItemSubType?: ModuleItemSubType, isDuplicate?: boolean): void {
    if (!this.isFocused) {
      this.isEditModuleItem = !!moduleItem?.id;
      this.dialog.open(ModuleItemDialogComponent, {
        data: {
          availableModuleItems: this.availableModuleItems,
          selectedStudy: this.selectedStudy,
          selectedModule: this.selectedModule,
          moduleItem,
          moduleItemSubType,
          selectedLanguage: this.selectedLanguage,
          isDuplicate,
        },
        minWidth: '95%',
        height: '95%',
      });
      this.previewService.setPreviewData({});
      this.dialog.afterAllClosed.subscribe(() => {
        this.store.dispatch(selectStudy({ studyId: this.selectedStudy.id }));
      });
    }
  }

  saveModuleSortingOrder(moduleItemsArray: ModuleItem[]) {
    const newModuleItemSortingOrder = getModuleItemSortOrderArray(moduleItemsArray);
    this.store.dispatch(
      saveModuleItemSortingOrder({
        studyId: this.selectedStudy?.id,
        moduleId: this.selectedModule?.id,
        sortingOrder: newModuleItemSortingOrder,
      })
    );
    this.store.dispatch(selectStudy({ studyId: this.selectedStudy.id }));
  }

  updateModuleItemCalculatedVariable(moduleItem: ModuleItem): void {
    this.store.dispatch(
      updateModuleItem({
        studyId: this.selectedStudy?.id,
        moduleId: this.selectedModule?.id,
        moduleItem,
      })
    );
    this.store.dispatch(selectStudy({ studyId: this.selectedStudy.id }));
  }

  updateCalculatedVariables(oldName: string, newName: string) {
    this.studyCalculatedVariables.forEach((calculatedVariable) => {
      if (calculatedVariable) {
        let calcVarNameWasUpdated = false;
        const regOldName = new RegExp('^' + oldName + '$');
        let formula = calculatedVariable.formula;

        this.calculatedVariableFunctions.forEach((value) => {
          if (this.lettersReg.test(value.calculationName)) {
            formula = formula.replace(value.calculationName, ' ');
            formula = formula.replace(')', ' ');
          } else {
            formula = formula.replace(new RegExp('\\' + value.calculationName, 'g'), ' ');
          }
        });

        const splitFormulaNames = formula.split(' ');
        let newFormula = calculatedVariable.formula;
        splitFormulaNames.forEach((splitValue, index) => {
          newFormula = newFormula.replace(splitValue, '#' + index);
        });

        splitFormulaNames.forEach((splitValue, index) => {
          if (splitValue.match(regOldName)) {
            splitFormulaNames[index] = newName;
            calcVarNameWasUpdated = true;
          }
        });

        if (calcVarNameWasUpdated) {
          splitFormulaNames.forEach((splitValue, index) => {
            newFormula = newFormula.replace('#' + index, splitValue);
          });

          const moduleItems = [];
          this.calculationNamesWithModuleItemIds.forEach((variable) => {
            if (calculatedVariable.formula.indexOf(variable.calculationName) !== -1) {
              moduleItems.push(variable.moduleItemId);
            }
          });
          calculatedVariable.formula = newFormula;
          calculatedVariable.moduleItems = moduleItems;
          this.store.dispatch(updateCalculatedVariable({ calculatedVariable }));
        }
      }
    });
    this.store.dispatch(selectStudy({ studyId: this.selectedStudy.id }));
  }

  deleteModuleItem(moduleItem: ModuleItem): void {
    this.store.dispatch(
      deleteModuleItem({
        studyId: this.selectedStudy?.id,
        moduleId: this.selectedModule?.id,
        moduleItemId: moduleItem?.id,
      })
    );
    this.calculatedVariables.removeAt(
      this.calculatedVariables.value.findIndex((calculatedName) => moduleItem?.calculationName === calculatedName)
    );
    this.store.dispatch(selectStudy({ studyId: this.selectedStudy.id }));
  }

  duplicateModuleItem(moduleItem: ModuleItem): void {
    this.itemToDuplicate = moduleItem;
    this.openModuleItemDialog(moduleItem, null, true);
  }

  getIconBySubType(moduleItemSubType: string): string {
    return getModuleItemIconBySubType(this.moduleItemsConfig, moduleItemSubType);
  }

  getSubTypeLabel(moduleItem: ModuleItem): string {
    return getModuleItemSubTypeLabel(this.moduleItemsConfig, moduleItem.type, moduleItem.subType);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onFocused(): void {
    this.isFocused = true;
  }

  outFocused(index): void {
    const inputCalculatedName = this.calculatedVariables.controls[index].value.calculatedName;
    const moduleItem = this.selectedModuleItems[index];
    this.isFocused = false;
    if (this.calculatedVariables.controls[index].get('calculatedName').valid) {
      const oldName = moduleItem.calculationName;
      if (inputCalculatedName !== oldName) {
        moduleItem.calculationName = inputCalculatedName;
        this.updateModuleItemCalculatedVariable(moduleItem);
        this.updateCalculatedVariables(oldName, inputCalculatedName);
      }
    }
  }
}
